<template>
    <div>
        <b-row>
            <!-- The inputs carry no bottom margin of their own, so each column does: it spaces
                 wrapped rows apart and leaves a gap above the Run button. -->
            <b-col v-for="parameter in visibleParameters" :key="parameter.name" cols="12" md="3" class="mb-3">
                <p-select
                    v-if="parameter.type === 'DatePreset' || parameter.type === 'Enum'"
                    :label="labelFor(parameter)"
                    :name="`agent-${parameter.name}`"
                    :options="optionsFor(parameter)"
                    :model-value="modelValue[parameter.name] ?? null"
                    no-selection-message=""
                    @update:model-value="set(parameter.name, $event)" />
                <p-input
                    v-else-if="parameter.type === 'Date'"
                    type="date"
                    :label="labelFor(parameter)"
                    :name="`agent-${parameter.name}`"
                    :model-value="modelValue[parameter.name] ?? ''"
                    @update:model-value="set(parameter.name, $event)" />
                <p-number
                    v-else-if="parameter.type === 'Money'"
                    :label="labelFor(parameter)"
                    :name="`agent-${parameter.name}`"
                    :model-value="modelValue[parameter.name] ?? null"
                    @update:model-value="setAmount(parameter.name, $event)" />
                <!-- A name that matched several: pick one, never guessed. -->
                <p-select
                    v-else-if="ambiguityFor(parameter)"
                    :label="`Which ${labelFor(parameter).toLowerCase()}?`"
                    :name="`agent-${parameter.name}`"
                    :options="choicesFor(parameter)"
                    :model-value="null"
                    no-selection-message=""
                    @update:model-value="pick(parameter, $event)" />
                <!-- The same lists Transaction Search uses, so a pick is always a real one. -->
                <p-select
                    v-else
                    :label="labelFor(parameter)"
                    :name="`agent-${parameter.name}`"
                    :data-type="dataTypeFor(parameter)"
                    :filter="filterFor(parameter)"
                    :add-invalid-option="extraOptionFor(parameter)"
                    :add-null-option="`All ${pluralFor(parameter)}`"
                    no-selection-message=""
                    :model-value="entityValue(parameter)"
                    @update:model-value="pickEntity(parameter, $event)" />
                <div v-if="errors[parameter.name]" class="small text-danger mt-1">{{ errors[parameter.name] }}</div>
            </b-col>
        </b-row>

        <div class="d-flex flex-wrap align-items-center gap-2">
            <p-button variant="primary" :is-busy="busy" @click="$emit('run')">Run report</p-button>
            <span v-if="dirty" class="small text-danger">Changed — run the report to apply.</span>
        </div>
    </div>
</template>

<script>
// The settings form for a server report tool, generated from the tool's own parameter
// metadata (GET reportagent/tools) - the same metadata the model reads and the server
// validates against, so the three cannot drift apart.
import { useAppStateStore } from '@/stores/appState';
import { useSelectListsStore } from '@/stores/selectLists';

const LABELS = {
    dateRange: 'Dates',
    fromDate: 'From',
    toDate: 'To',
    transactionType: 'Type',
    minTotal: 'Minimum total',
    maxTotal: 'Maximum total',
    register: 'Register',
    cashier: 'Cashier',
    tender: 'Tender',
    arAccount: 'AR account',
};

const PLURALS = { register: 'registers', cashier: 'cashiers', tender: 'tenders', arAccount: 'AR accounts' };

// The select lists Transaction Search uses for the same filters; their values are the ids
// the report filters on (RegisterId, NbcUserId, TenderId, AccountId).
const DATA_TYPES = { register: 'posDevicesWithLocation', cashier: 'nbcUsers', tender: 'tenders', arAccount: 'accounts' };

// Stands for a name the AI gave that matched nothing, so it shows as selected until replaced.
const UNMATCHED = -1;

const PRESET_LABELS = {
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This week',
    lastWeek: 'Last week',
    previous7Days: 'Previous 7 days',
    thisMonth: 'This month',
    lastMonth: 'Last month',
    yearToDate: 'Year to date',
    lastYear: 'Last year',
    custom: 'Custom dates',
};

export default {
    name: 'TransactionsParameters',
    props: {
        modelValue: { type: Object, required: true },
        tool: { type: Object, required: true },
        errors: { type: Object, default: () => ({}) },
        ambiguities: { type: Array, default: () => [] },
        busy: { type: Boolean, default: false },
        dirty: { type: Boolean, default: false },
    },
    emits: ['update:modelValue', 'run'],
    computed: {
        // Custom from/to dates only matter for a custom range.
        visibleParameters() {
            const isCustom = this.modelValue.dateRange === 'custom';
            return this.tool.parameters.filter(x => isCustom || x.type !== 'Date');
        },
    },
    methods: {
        labelFor(parameter) {
            return LABELS[parameter.name] || parameter.name;
        },
        pluralFor(parameter) {
            return PLURALS[parameter.name] || 'values';
        },
        optionsFor(parameter) {
            if (parameter.type === 'DatePreset')
                return parameter.allowedValues.map(x => ({ value: x, text: PRESET_LABELS[x] || x }));

            return [{ value: null, text: 'All types' }, ...parameter.allowedValues.map(x => ({ value: x, text: x }))];
        },
        // Still ambiguous only while the value is the name that was ambiguous.
        ambiguityFor(parameter) {
            const value = this.modelValue[parameter.name];
            return this.ambiguities.find(x => x.parameter === parameter.name && x.value === value) || null;
        },
        choicesFor(parameter) {
            return this.ambiguityFor(parameter).choices.map(x => ({ value: x.id, text: x.label }));
        },
        dataTypeFor(parameter) {
            return DATA_TYPES[parameter.name];
        },
        // Registers belong to a store: with one selected, only its registers are offered.
        filterFor(parameter) {
            const locationId = useAppStateStore().getSelectedLocationId;
            if (parameter.name !== 'register' || !locationId)
                return null;

            return option => option.filter === String(locationId);
        },
        // The id a resolved setting holds, the stand-in for an unmatched name, or null for "All".
        entityValue(parameter) {
            const value = this.modelValue[parameter.name];
            if (value && typeof value === 'object')
                return value.id;

            return typeof value === 'string' ? UNMATCHED : null;
        },
        // An option the list would not show but the setting holds, so it is never silently
        // cleared: a name the AI gave that matched nothing, or a saved register, cashier or
        // account the list leaves out (disabled, inactive, or at another store).
        extraOptionFor(parameter) {
            const value = this.modelValue[parameter.name];
            if (typeof value === 'string')
                return { value: UNMATCHED, text: `"${value}" - not found` };

            if (!value || typeof value !== 'object')
                return null;

            const filter = this.filterFor(parameter);
            const listed = (this.listFor(parameter) || []).some(x => x.value === value.id && (!filter || filter(x)));
            return listed ? null : { value: value.id, text: value.label };
        },
        listFor(parameter) {
            return useSelectListsStore().lists[DATA_TYPES[parameter.name]];
        },
        pickEntity(parameter, selected) {
            if (selected === UNMATCHED)
                return;

            if (selected === null || selected === undefined || selected === '') {
                this.set(parameter.name, null);
                return;
            }

            const id = Number(selected);
            const current = this.modelValue[parameter.name];
            const option = (this.listFor(parameter) || []).find(x => x.value === id);
            const label = option ? option.text : current && current.id === id ? current.label : String(id);
            this.set(parameter.name, { id, label });
        },
        pick(parameter, id) {
            const choice = this.ambiguityFor(parameter).choices.find(x => x.id === Number(id));
            if (!choice)
                return;

            this.set(parameter.name, { id: choice.id, label: choice.label });
        },
        setAmount(name, value) {
            this.set(name, value === '' || value === null || value === undefined ? null : Number(value));
        },
        // An emptied setting is removed, so the server sees "not set" rather than an empty string.
        set(name, value) {
            const next = { ...this.modelValue };
            if (value === null || value === undefined || value === '')
                delete next[name];
            else
                next[name] = value;

            // Leaving a custom range: its dates are hidden now, so they go too.
            if (name === 'dateRange' && value !== 'custom') {
                delete next.fromDate;
                delete next.toDate;
            }

            this.$emit('update:modelValue', next);
        },
    },
};
</script>
